import dayjs from 'dayjs';
import * as R from 'ramda';
import { RRule, rrulestr } from 'rrule';
import {
  HolidayExecutionType,
  CreateScheduledEventMetadataInput,
  _Boolean,
} from '@/API';
import {
  fetchScheduledHolidayMetadatas,
  fetchPublicHolidays,
} from '@/appsync/service/EventCalendarQueryService';
import { i18n } from '@/plugins/i18n/i18n';
import {
  LoginInfo,
  PublicHolidayForFront,
  ScheduledHolidayMetadataForFront,
  ScheduledEventMetadataWithDetailForFront,
} from '@/types/type';
import { PortalDate, newPortalDate } from '@/utils/PortalDate';
import { getRRuleSet, getRRuleSetArgs } from '@/utils/RRuleUtils';
import { TriggerOnHolidays } from '@/utils/consts';

export const isRelatedToToday = async (
  loginInfo: LoginInfo,
  listOfScheduledEventMetadata: (ScheduledEventMetadataWithDetailForFront | null)[]
) => {
  for (const item of listOfScheduledEventMetadata) {
    if (item === null) {
      continue;
    }
    const rRuleSet = getRRuleSet(getRRuleSetArgs(item));
    const checkTheOccurrence = await doesTheOccurrencesIncludeToday(
      item,
      loginInfo.tenant,
      loginInfo.landscape,
      rRuleSet,
      item.holidayExecutionType
    );
    if (checkTheOccurrence) {
      return true;
    }
  }
  return false;
};

export const doesTheOccurrencesIncludeToday = async (
  scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
  tenant: string,
  landscape: string,
  rruleSet: string,
  holidayExecutionType: HolidayExecutionType
) => {
  const targetDate = newPortalDate(dayjs().startOf('day'));
  const scheduledHolidayMetadatas = await fetchScheduledHolidayMetadatas(
    tenant,
    landscape
  );
  const publicHolidays = await fetchPublicHolidays(i18n.locale);

  const isHolidayForThisEventMetadata = isHoliday(
    scheduledEventMetadata,
    scheduledHolidayMetadatas,
    publicHolidays
  );
  const hasOccurrencesForThisEventMetadata = hasOccurrences(rruleSet);

  switch (holidayExecutionType) {
    case HolidayExecutionType.NoChange:
      return hasOccurrencesForThisEventMetadata(targetDate);
    case HolidayExecutionType.Skip:
      return (
        hasOccurrencesForThisEventMetadata(targetDate) &&
        !isHolidayForThisEventMetadata(targetDate)
      );
    case HolidayExecutionType.TriggerBeforeHoliday:
      if (!isHolidayForThisEventMetadata(targetDate)) {
        if (hasOccurrencesForThisEventMetadata(targetDate)) {
          return true;
        } else {
          const holidays = getContinuesNextHolidays(
            targetDate,
            scheduledEventMetadata,
            scheduledHolidayMetadatas,
            publicHolidays
          );
          if (R.any<PortalDate>(hasOccurrencesForThisEventMetadata, holidays)) {
            return true;
          }
          return false;
        }
      }
      break;
    case HolidayExecutionType.TriggerAfterHoliday:
      if (!isHolidayForThisEventMetadata(targetDate)) {
        if (hasOccurrencesForThisEventMetadata(targetDate)) {
          return true;
        } else {
          const holidays = getContinuesPrevHolidays(
            targetDate,
            scheduledEventMetadata,
            scheduledHolidayMetadatas,
            publicHolidays
          );
          if (R.any<PortalDate>(hasOccurrencesForThisEventMetadata, holidays)) {
            return true;
          }
          return false;
        }
      }
      break;
    default: {
      const inspector: never = holidayExecutionType;
      return false;
    }
  }
};

export const getContinuesPrevHolidays = (
  targetDate: PortalDate,
  scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
  scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[],
  publicHolidays: PublicHolidayForFront[]
) => {
  const isHolidayForThisEventMetadata = isHoliday(
    scheduledEventMetadata,
    scheduledHolidayMetadatas,
    publicHolidays
  );
  const continuesPrevHolidays = [];
  while (isHolidayForThisEventMetadata(targetDate.subtract(1, 'day'))) {
    continuesPrevHolidays.push(targetDate.subtract(1, 'day'));
    targetDate = targetDate.subtract(1, 'day');
  }
  return continuesPrevHolidays;
};

export const getContinuesNextHolidays = (
  targetDate: PortalDate,
  scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
  scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[],
  publicHolidays: PublicHolidayForFront[]
) => {
  const isHolidayForThisEventMetadata = isHoliday(
    scheduledEventMetadata,
    scheduledHolidayMetadatas,
    publicHolidays
  );
  const continuesNextHolidays = [];
  while (isHolidayForThisEventMetadata(targetDate.add(1, 'day'))) {
    continuesNextHolidays.push(targetDate.add(1, 'day'));
    targetDate = targetDate.add(1, 'day');
  }
  return continuesNextHolidays;
};

export const getDateTime = (rruleString: string, targetDate: PortalDate) => {
  const timeOffset = new Date(Date.now()).getTimezoneOffset();
  const adjustedTargetDate = targetDate.subtract(timeOffset, 'minute');
  const rrule: RRule = rrulestr(rruleString);
  const occurrences = rrule.between(
    adjustedTargetDate.toDate(),
    adjustedTargetDate
      .add(1, 'day')
      .subtract(1, 'second')
      .toDate(),
    true
  );

  if (R.isEmpty(occurrences)) {
    return false;
  }

  return true;
};

export const hasOccurrences = R.curry(
  (rruleString: string, targetDate: PortalDate) =>
    getDateTime(rruleString, targetDate)
);

export const getHolidays = (
  scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
  scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[],
  publicHolidays: PublicHolidayForFront[]
) => {
  const labels = scheduledEventMetadata.customHolidayLabels;
  const isLabelIncluded = (label: string) => R.includes(label, labels);
  const filteredScheduledHolidayMetadatas = R.filter(
    R.propSatisfies(R.any(isLabelIncluded), 'labels'),
    scheduledHolidayMetadatas
  );
  const filteredPublicHolidays = R.filter(
    R.propSatisfies(isLabelIncluded, 'label'),
    publicHolidays
  );

  switch (scheduledEventMetadata.triggerOnHolidays) {
    case TriggerOnHolidays.allHolidays:
      return {
        scheduledHolidayMetadatas,
        publicHolidays,
      };
    case TriggerOnHolidays.customHolidays:
      if (R.isNil(labels)) {
        return {
          // no labels means no holidays
          scheduledHolidayMetadatas: [],
          publicHolidays: [],
        };
      }
      return {
        scheduledHolidayMetadatas: filteredScheduledHolidayMetadatas,
        publicHolidays: filteredPublicHolidays,
      };
    default: {
      const inspector: never = scheduledEventMetadata.triggerOnHolidays;
      return {
        scheduledHolidayMetadatas,
        publicHolidays,
      };
    }
  }
};

export const isHoliday = R.curry(
  (
    scheduledEventMetadata: ScheduledEventMetadataWithDetailForFront,
    scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[],
    publicHolidays: PublicHolidayForFront[],
    targetDate: PortalDate
  ) => {
    const holidays = getHolidays(
      scheduledEventMetadata,
      scheduledHolidayMetadatas,
      publicHolidays
    );
    return (
      R.any<ScheduledHolidayMetadataForFront>(
        h => hasOccurrences(h.rruleSet, targetDate),
        holidays.scheduledHolidayMetadatas
      ) ||
      R.any<PublicHolidayForFront>(
        p => targetDate.isSameDay(p.startDate),
        holidays.publicHolidays
      )
    );
  }
);
